import React from "react";
import PropTypes from "prop-types";
import {FormattedNumber} from "./FormattedNumber";

const fsMap = {small: "fs-6", medium: "fs-5", large: "fs-3", extralarge: "fs-2"};
const coerceNumber = (v, fallback = 0) =>
    Number.isFinite(Number(v)) ? Number(v) : fallback;

/**
 * Price component used to display formatted pricing information, including the selling price
 * and optionally the base price if a discount is present.
 *
 * @param {Object} props - The input properties.
 * @param {Object} props.data - The data object containing price values.
 * @param {number|string} [props.data.sellingPrice] - The selling price to be displayed. Defaults to 0 if not provided.
 * @param {number|string} [props.data.basePrice] - The base price for comparison purposes. Defaults to 0 if not provided.
 * @param {Object} props.config - The configuration object for formatting and presentation.
 * @param {number} [props.config.precision=2] - The number of decimal places to display for the price.
 * @param {string} [props.config.size="medium"] - The size of the price text, e.g., "small", "medium", or "large".
 * @param {string} [props.config.locale="es-ES"] - The locale string for number formatting.
 * @param {string} [props.config.currency="EUR"] - The currency code (e.g., "USD", "EUR") for formatting the price.
 * @returns {JSX.Element} A React component displaying the formatted selling price and, if applicable, the base price with a discount indication.
 */
export const Price = ({
                          data: {sellingPrice: spRaw, basePrice: bpRaw} = {},
                          config: {
                              precision = 2,
                              size = "medium",
                              locale = "es-ES",
                              currency = "EUR",
                          } = {},
                      }) => {

    const sellingPrice = coerceNumber(spRaw, 0);
    const basePrice = coerceNumber(bpRaw, 0);
    const hasDiscount = sellingPrice > 0 && basePrice> 0 && basePrice > sellingPrice;

    const sizeClass = fsMap[size] || fsMap.medium;

    return (
        <div className="d-flex align-items-end gap-2">
            <strong className={sizeClass}>
                <FormattedNumber
                    value={sellingPrice}
                    config={{precision, locale, style: "currency", currency}}
                />
            </strong>

            {hasDiscount && (
                <small className="text-danger text-decoration-line-through">
                    <FormattedNumber
                        value={basePrice}
                        config={{precision, locale, style: "currency", currency}}
                    />
                </small>
            )}
        </div>
    );
};

Price.propTypes = {
    data: PropTypes.shape({
        sellingPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        basePrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    config: PropTypes.shape({
        precision: PropTypes.number,
        size: PropTypes.oneOf(["small", "medium", "large", "extralarge"]),
        locale: PropTypes.oneOf(["es-ES", "en-GB", "fr-FR"]),
        currency: PropTypes.oneOf(["EUR"]),
    }),
};
